/**
 * RegisterPage - 注册页面
 * 现代化注册界面，符合项目整体 UI 风格
 */
import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  UserPlus, User, Mail, Lock, Eye, EyeOff, 
  AlertCircle, Loader2, Brain, Check, X 
} from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useDebounce } from '../hooks/useDebounce'

export default function RegisterPage() {
  const { register, checkUsernameAvailable, checkEmailAvailable, isLoading, error, clearError } = useAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const redirectTo = searchParams.get('redirect') || '/dashboard'
  
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [touched, setTouched] = useState({ 
    username: false, 
    email: false, 
    password: false,
    confirmPassword: false 
  })
  
  // 可用性检查状态
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null)
  const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null)
  const [checkingUsername, setCheckingUsername] = useState(false)
  const [checkingEmail, setCheckingEmail] = useState(false)
  
  // 防抖
  const debouncedUsername = useDebounce(username, 500)
  const debouncedEmail = useDebounce(email, 500)
  
  // 验证
  const isUsernameValid = /^[a-zA-Z0-9_-]{3,20}$/.test(username)
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const isPasswordValid = password.length >= 8
  const isPasswordMatch = password === confirmPassword
  
  const canSubmit = isUsernameValid && isEmailValid && isPasswordValid && isPasswordMatch 
    && usernameAvailable !== false && emailAvailable !== false && !isLoading
  
  // 检查用户名可用性
  useEffect(() => {
    if (debouncedUsername && isUsernameValid) {
      setCheckingUsername(true)
      checkUsernameAvailable(debouncedUsername)
        .then(available => setUsernameAvailable(available))
        .finally(() => setCheckingUsername(false))
    } else {
      setUsernameAvailable(null)
    }
  }, [debouncedUsername, isUsernameValid, checkUsernameAvailable])
  
  // 检查邮箱可用性
  useEffect(() => {
    if (debouncedEmail && isEmailValid) {
      setCheckingEmail(true)
      checkEmailAvailable(debouncedEmail)
        .then(available => setEmailAvailable(available))
        .finally(() => setCheckingEmail(false))
    } else {
      setEmailAvailable(null)
    }
  }, [debouncedEmail, isEmailValid, checkEmailAvailable])
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    
    try {
      await register(username, email, password)
      // 注册成功后跳转到 redirect 地址（或首页）
      navigate(redirectTo)
    } catch {
      // 错误已在 hook 中处理
    }
  }
  
  // 可用性指示器
  const AvailabilityIndicator = ({ 
    checking, 
    available, 
    show 
  }: { 
    checking: boolean
    available: boolean | null
    show: boolean 
  }) => {
    if (!show) return null
    
    if (checking) {
      return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
    }
    
    if (available === true) {
      return <Check className="w-4 h-4 text-green-400" />
    }
    
    if (available === false) {
      return <X className="w-4 h-4 text-red-400" />
    }
    
    return null
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Logo & 标题 */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.1 }}
            className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-lg shadow-primary-500/30 mb-4"
          >
            <Brain className="w-8 h-8 text-white" />
          </motion.div>
          <h1 className="text-2xl font-bold text-white mb-2">创建账号</h1>
          <p className="text-slate-400">开始使用 ToB 销售 AI 助手</p>
        </div>
        
        {/* 注册表单 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-xl"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 错误提示 */}
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </motion.div>
            )}
            
            {/* 用户名 */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                用户名
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value)
                    clearError()
                  }}
                  onBlur={() => setTouched(t => ({ ...t, username: true }))}
                  placeholder="3-20字符，字母数字下划线"
                  className={`w-full pl-10 pr-10 py-3 bg-white/5 border rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 transition-all ${
                    touched.username && (!isUsernameValid || usernameAvailable === false)
                      ? 'border-red-500/50 focus:ring-red-500/30'
                      : 'border-white/10 focus:ring-primary-500/30 focus:border-primary-500/50'
                  }`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <AvailabilityIndicator 
                    checking={checkingUsername} 
                    available={usernameAvailable}
                    show={touched.username && isUsernameValid}
                  />
                </div>
              </div>
              {touched.username && !isUsernameValid && (
                <p className="mt-1 text-xs text-red-400">用户名需为3-20位字母、数字、下划线或连字符</p>
              )}
              {touched.username && isUsernameValid && usernameAvailable === false && (
                <p className="mt-1 text-xs text-red-400">用户名已被使用</p>
              )}
            </div>
            
            {/* 邮箱 */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                邮箱
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    clearError()
                  }}
                  onBlur={() => setTouched(t => ({ ...t, email: true }))}
                  placeholder="your@email.com"
                  className={`w-full pl-10 pr-10 py-3 bg-white/5 border rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 transition-all ${
                    touched.email && (!isEmailValid || emailAvailable === false)
                      ? 'border-red-500/50 focus:ring-red-500/30'
                      : 'border-white/10 focus:ring-primary-500/30 focus:border-primary-500/50'
                  }`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <AvailabilityIndicator 
                    checking={checkingEmail} 
                    available={emailAvailable}
                    show={touched.email && isEmailValid}
                  />
                </div>
              </div>
              {touched.email && !isEmailValid && (
                <p className="mt-1 text-xs text-red-400">请输入有效的邮箱地址</p>
              )}
              {touched.email && isEmailValid && emailAvailable === false && (
                <p className="mt-1 text-xs text-red-400">邮箱已被注册</p>
              )}
            </div>
            
            {/* 密码 */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                密码
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    clearError()
                  }}
                  onBlur={() => setTouched(t => ({ ...t, password: true }))}
                  placeholder="至少8位"
                  className={`w-full pl-10 pr-12 py-3 bg-white/5 border rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 transition-all ${
                    touched.password && !isPasswordValid
                      ? 'border-red-500/50 focus:ring-red-500/30'
                      : 'border-white/10 focus:ring-primary-500/30 focus:border-primary-500/50'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {touched.password && !isPasswordValid && (
                <p className="mt-1 text-xs text-red-400">密码至少需要8位</p>
              )}
            </div>
            
            {/* 确认密码 */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                确认密码
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    clearError()
                  }}
                  onBlur={() => setTouched(t => ({ ...t, confirmPassword: true }))}
                  placeholder="再次输入密码"
                  className={`w-full pl-10 pr-4 py-3 bg-white/5 border rounded-xl text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 transition-all ${
                    touched.confirmPassword && !isPasswordMatch
                      ? 'border-red-500/50 focus:ring-red-500/30'
                      : 'border-white/10 focus:ring-primary-500/30 focus:border-primary-500/50'
                  }`}
                />
              </div>
              {touched.confirmPassword && !isPasswordMatch && (
                <p className="mt-1 text-xs text-red-400">两次输入的密码不一致</p>
              )}
            </div>
            
            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={!canSubmit}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium transition-all mt-6 ${
                canSubmit
                  ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700 shadow-lg shadow-primary-500/30'
                  : 'bg-slate-700 text-slate-400 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <UserPlus className="w-5 h-5" />
                  注册
                </>
              )}
            </button>
          </form>
          
          {/* 登录链接 */}
          <div className="mt-6 pt-6 border-t border-white/10 text-center">
            <p className="text-slate-400 text-sm">
              已有账号？{' '}
              <Link 
                to={redirectTo !== '/' ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'} 
                className="text-primary-400 hover:text-primary-300 font-medium transition-colors"
              >
                立即登录
              </Link>
            </p>
          </div>
        </motion.div>
        
        {/* 底部版权 */}
        <p className="mt-8 text-center text-slate-500 text-sm">
          © 2026 ToB Sales AI Assistant
        </p>
      </motion.div>
    </div>
  )
}

